import React, { useState, useEffect } from "react";
import { createJobAPI } from "../../../services/jobService";
import { showAppAlert } from "../../../store/reducers/appreducer";
import { useDispatch } from "react-redux";
import { styled } from "@mui/system";
import {
  Typography,
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  Slider,
  Snackbar,
} from "@mui/material";


const FormContainer = styled(Paper)({
  padding: "50px",
  marginBottom: "20px",
  borderRadius: "14px",
});

const FormTitle = styled(Typography)({
  fontFamily: "Raleway",
  fontSize: "21px",
  color: "#304C68",
  fontWeight: 600,
  lineHeight: "24.65px",
  marginBottom: "20px",
});

const FormGroup = styled(Box)({
  marginBottom: "20px",
});

const FormLabel = styled(Typography)({
  fontFamily: "Raleway",
  fontWeight: 500,
  fontSize: "14px",
  color: "#000",
  lineHeight: "22.4px",
});

const ButtonContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  marginTop: "20px",
});

const CreateTabButton = styled(Button)({
  width: "294px",
  height: "56px",
  fontFamily: "Raleway",
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "18.78px",
  borderRadius: "8px",
  padding: "8px",
});

const CreateJobForm = ({ onSave, onCancel, onNavigate }) => {
  const dispatch = useDispatch(); // Initialize dispatch hook for Redux
  const [jobCreated, setJobCreated] = useState(false); // State to track job creation
  const [jobs, setJobs] = useState([]);
  const [dualModelScoring, setDualModelScoring] = useState(false); // New state for dual model scoring
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    company: "",
    location: "",
    // department: "",
    // type: "",
    // startDate: null,

    // Weighting Parameters State
    weighting_parameters: {
      relevance: 0.2,
      skills: 0.2,
      education: 0.2,
      work_experience: 0.2,
      achievements: 0.2,
    },
  });

  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const handleSliderChange = (name) => (event, value) => {
    setFormData((prevData) => ({
      ...prevData,
      weighting_parameters: {
        ...prevData.weighting_parameters,
        [name]: value,
      },
    }));
  };

  // const handleStartDatePickerChange = (date) => {
  //   setFormData({
  //     ...formData,
  //     startDate: date,
  //   });
  // };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleSave = async () => {
    let errorpresent = false;
    const weightingParams = formData.weighting_parameters;

    // Calculate the sum of the user-selected weighting parameters
    const sum =
      weightingParams.relevance +
      weightingParams.skills +
      weightingParams.education +
      weightingParams.work_experience +
      weightingParams.achievements;

    // Normalize the values if the sum is not 1
    const normalizedParams = {
      relevance: weightingParams.relevance / sum,
      skills: weightingParams.skills / sum,
      education: weightingParams.education / sum,
      work_experience: weightingParams.work_experience / sum,
      achievements: weightingParams.achievements / sum,
    };

    const newErrors = {};
    const mandatoryFields = ["title", "description", "company", "location"];

    mandatoryFields.forEach((key) => {
      if (!formData[key] || formData[key].trim() === "") {
        errorpresent = true;
        newErrors[key] = "This field is required";
      }
    })    

    if (errorpresent) {
      setErrors(newErrors);
      return;
    }

    const jobData = {
      title: formData.title,
      description: formData.description,
      company: formData.company,
      location: formData.location,
      weighting_parameters: normalizedParams, // Send normalized parameters
      // original_parameters: originalParams, // Store original values for editing
    };

    const apiPayload = {
      description: jobData.description,
      weighting_parameters: jobData.weighting_parameters,
      job_title: jobData.title,
      location: jobData.location,
      company_name: jobData.company,
      ...(dualModelScoring && { dual_model_scoring:true }), // Only adds if true
    };


    try {
      await createJobAPI(apiPayload);

      // Reset form after successful job creation
      setFormData({
        title: "",
        description: "",
        company: "",
        location: "",
        weighting_parameters: {
          relevance: 0.2,
          skills: 0.2,
          education: 0.2,
          work_experience: 0.2,
          achievements: 0.2,
        },
      });
      setDualModelScoring(false);

      // Provide feedback to user
      dispatch(
        showAppAlert({
          show: true,
          alertType: "success",
          alertMessage: "Job created successfully",
        })
      );
      setJobCreated(true); // Set state to true after job creation

      // Navigate to another route or perform the original functionality
      if (onSave) {
        onSave(jobData); // Call the original onSave function if it exists
      }

      if (onNavigate) {
        onNavigate(); // Navigate if the original logic was calling a navigation method
      }
    } catch (error) {
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: "Failed to create job. Please try again.",
        })
      );
    }
  };

  // Marks array to display 0, 0.1, 0.2, ..., 1 on sliders
  const marks = [
    { value: 0, label: "0" },
    { value: 0.1, label: "0.1" },
    { value: 0.2, label: "0.2" },
    { value: 0.3, label: "0.3" },
    { value: 0.4, label: "0.4" },
    { value: 0.5, label: "0.5" },
    { value: 0.6, label: "0.6" },
    { value: 0.7, label: "0.7" },
    { value: 0.8, label: "0.8" },
    { value: 0.9, label: "0.9" },
    { value: 1, label: "1" },
  ];
  // useEffect(() => {
  //   const fetchJobs = async () => {
  //     try {
  //       const response = await fetchJobAPI();
  //       console.log(response.Jobs[0]['Job Id']);
  //       setJobs(Array.isArray(response.Jobs) ? response.Jobs : []);
  //     } catch (error) {
  //       console.error("Error fetching jobs:", error);
  //     }
  //   };

  //   fetchJobs();
  // }, []);
  return (
    <FormContainer>
      <FormTitle>Create Job</FormTitle>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel variant="subtitle1">Title*</FormLabel>
            <TextField
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter Title"
              variant="outlined"
              fullWidth
              error={!!errors.title}
              helperText={errors.title}
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel variant="subtitle1">Company*</FormLabel>
            <TextField
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              placeholder="Enter Company Name"
              variant="outlined"
              fullWidth
              required
              error={!!errors.company}
              helperText={errors.company}
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel variant="subtitle1">Location*</FormLabel>
            <TextField
              name="location"
              placeholder="Enter Location"
              value={formData.location}
              onChange={handleInputChange}
              variant="outlined"
              fullWidth
              required
              error={!!errors.location}
              helperText={errors.location}
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel variant="subtitle1">Scoring Mode</FormLabel>
            <TextField
              select
              // label="Select Scoring Mode"
              value={dualModelScoring ? "Use Dual Model" : "No Dual Model"}
              onChange={(e) =>
                setDualModelScoring(e.target.value === "Use Dual Model")
              }
              variant="outlined"
              fullWidth
            >
              <MenuItem value="Use Dual Model">
                Use Dual Model for Scoring
              </MenuItem>
              <MenuItem value="No Dual Model">
                No Dual Model for Scoring
              </MenuItem>
            </TextField>
          </FormGroup>
        </Grid>
        <Grid item xs={12}>
          <FormGroup>
            <FormLabel variant="subtitle1">Description*</FormLabel>
            <TextField
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter Job Description"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              required
              error={!!errors.description}
              helperText={errors.description}
            />
          </FormGroup>
        </Grid>

        {/* Weighting Parameters Sliders */}
        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel variant="subtitle1">Relevance</FormLabel>
            <Slider
              value={formData.weighting_parameters.relevance}
              onChange={handleSliderChange("relevance")}
              step={0.1}
              min={0}
              max={1}
              marks={marks}
              valueLabelDisplay="auto"
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel variant="subtitle1">Skills</FormLabel>
            <Slider
              value={formData.weighting_parameters.skills}
              onChange={handleSliderChange("skills")}
              step={0.1}
              min={0}
              max={1}
              marks={marks}
              valueLabelDisplay="auto"
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel variant="subtitle1">Education</FormLabel>
            <Slider
              value={formData.weighting_parameters.education}
              onChange={handleSliderChange("education")}
              step={0.1}
              min={0}
              max={1}
              marks={marks}
              valueLabelDisplay="auto"
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel variant="subtitle1">Work Experience</FormLabel>
            <Slider
              value={formData.weighting_parameters.work_experience}
              onChange={handleSliderChange("work_experience")}
              step={0.1}
              min={0}
              max={1}
              marks={marks}
              valueLabelDisplay="auto"
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel variant="subtitle1">Achievements</FormLabel>
            <Slider
              value={formData.weighting_parameters.achievements}
              onChange={handleSliderChange("achievements")}
              step={0.1}
              min={0}
              max={1}
              marks={marks}
              valueLabelDisplay="auto"
            />
          </FormGroup>
        </Grid>
      </Grid>

      <ButtonContainer>
        <CreateTabButton
          onClick={handleSave}
          sx={{
            mr: 2,
            backgroundColor: "#5088FF",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#4C679D",
            },
          }}
        >
          Save
        </CreateTabButton>
        <CreateTabButton
          onClick={onCancel}
          variant="contained"
          style={{ backgroundColor: "#D9D9D9", color: "black" }}
        >
          Cancel
        </CreateTabButton>
      </ButtonContainer>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message="Sum of weighting parameters must be 1"
      />
    </FormContainer>
  );
};

export default CreateJobForm;
